import React from 'react';
import styled from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import { ETheme } from '@datatype/type';
import * as color from '@constant/color';
import * as size from '@constant/size';

export interface IPhaseProgressItem {
  id: string;
  label: string;
}

interface IProps {
  items: IPhaseProgressItem[];
  currentId: string;
  completed?: boolean;
  completedIds?: string[];
  visitedIds?: string[];
  enabledIds?: string[];
  handlePhaseClick?: (id: string) => void;
  theme?: ETheme;
  size?: 'small' | 'medium' | 'large';
  width?: string;
  height?: string;
}

const createDesign = (
  componentSize: IProps['size'],
  theme: ETheme,
  width?: string,
  height?: string,
  clickable = false,
) => {
  const createSize = () => {
    const diameter = componentSize === 'small' ? '24px' : componentSize === 'large' ? '36px' : '30px';
    return {
      diameter,
      gap: componentSize === 'small' ? '5px' : size.MARGIN_1,
      borderWidth: size.DESIGN_SYSTEM_BUTTON_BORDER_WIDTH,
      textSize: componentSize === 'large' ? ('medium' as const) : ('small' as const),
    };
  };
  const createTheme = () => {
    const palette: Partial<Record<ETheme, string>> = {
      [ETheme.BLACK]: color.BLACK_2,
      [ETheme.GRAY]: color.GRAY_1,
      [ETheme.BROWN]: color.BROWN_1,
      [ETheme.CREAM]: color.BROWN_2,
      [ETheme.BLUE]: color.BLUE_2,
      [ETheme.CYAN]: color.CYAN_2,
      [ETheme.GREEN]: color.GREEN_3,
      [ETheme.RED]: color.RED_2,
      [ETheme.YELLOW]: color.YELLOW_2,
      [ETheme.WHITE]: color.WHITE_1,
    };
    return {
      activeColor: palette[theme] ?? color.BROWN_1,
      pendingColor: color.GRAY_1,
      trackColor: color.GRAY_3,
      surfaceColor: color.WHITE_1,
      activeTextColor: theme === ETheme.WHITE || theme === ETheme.YELLOW ? color.BLACK_2 : color.WHITE_1,
    };
  };
  const createEvent = () => ({ cursor: clickable ? 'pointer' : 'default' });
  return { ...createSize(), ...createTheme(), event: createEvent(), width, height };
};
type TDesign = ReturnType<typeof createDesign>;
const Wrapper = styled.ol<{ $design: TDesign }>`
  display: flex;
  flex-flow: row nowrap;
  width: ${({ $design }) => $design.width || 'fit-content'};
  height: ${({ $design }) => $design.height || 'auto'};
  margin: 0;
  padding: 0;
  list-style: none;
  cursor: ${({ $design }) => $design.event.cursor};
`;
const PhaseItemContainer = styled.li<{ $design: TDesign }>`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: ${({ $design }) => $design.gap};
  flex: 1;
  min-width: ${({ $design }) => $design.diameter};
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    height: ${({ $design }) => $design.borderWidth};
    width: calc(100% - ${({ $design }) => $design.diameter});
    top: calc(${({ $design }) => $design.diameter} / 2);
    left: calc(50% + ${({ $design }) => $design.diameter} / 2);
    background: ${({ $design }) => $design.trackColor};
  }
`;
const PhaseButton = styled.button<{ $design: TDesign }>`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
  gap: ${({ $design }) => $design.gap};
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  cursor: ${({ $design }) => $design.event.cursor};
`;
const Node = styled.div<{ $design: TDesign & { filled: boolean; done: boolean; visited: boolean } }>`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${({ $design }) => $design.diameter};
  height: ${({ $design }) => $design.diameter};
  border-radius: 50%;
  border: ${({ $design }) => $design.borderWidth} solid
    ${({ $design }) => ($design.filled || $design.done || $design.visited ? $design.activeColor : $design.trackColor)};
  background: ${({ $design }) => ($design.filled ? $design.activeColor : $design.surfaceColor)};
  transition:
    background-color 180ms ease,
    border-color 180ms ease;
`;

// 클릭 핸들러가 없으면 표시 전용. 완료 시 모든 노드가 체크된다.
const PhaseProgress: React.FC<IProps> = ({
  items,
  currentId,
  completed = false,
  completedIds,
  visitedIds,
  enabledIds,
  theme = ETheme.BROWN,
  size: componentSize = 'medium',
  width,
  height,
  handlePhaseClick,
}) => {
  const design = createDesign(componentSize, theme, width, height, !!handlePhaseClick);
  const currentIndex = items.findIndex((item) => item.id === currentId);
  const PhaseItems = items.map((item, index) => {
    const done = completedIds ? completedIds.includes(item.id) : completed || index < currentIndex;
    const clickable = !!handlePhaseClick && (!enabledIds || enabledIds.includes(item.id));
    const visited = visitedIds?.includes(item.id) ?? false;
    const current = !completed && item.id === currentId;
    return (
      <PhaseItemContainer key={item.id} $design={design}>
        <PhaseButton
          as={handlePhaseClick ? 'button' : 'div'}
          type={handlePhaseClick ? 'button' : undefined}
          $design={{ ...design, event: { cursor: clickable ? 'pointer' : 'default' } }}
          disabled={handlePhaseClick ? !clickable : undefined}
          onClick={clickable ? () => handlePhaseClick?.(item.id) : undefined}>
          <Node $design={{ ...design, filled: current, done, visited }}>
            {done ? (
              <IconBlock
                icon='check'
                design={{
                  icon: {
                    size: componentSize === 'small' ? 'tiny' : 'small',
                    color: current ? design.activeTextColor : design.activeColor,
                    kind: 'outlined',
                  },
                }}
              />
            ) : (
              <TextBlock
                text={String(index + 1)}
                design={{
                  text: {
                    size: design.textSize,
                    color: current ? design.activeTextColor : visited ? design.activeColor : design.pendingColor,
                    bold: true,
                  },
                }}
              />
            )}
          </Node>
          <TextBlock
            text={item.label}
            design={{
              text: { size: design.textSize, color: current ? design.activeColor : design.pendingColor, bold: current },
            }}
          />
        </PhaseButton>
      </PhaseItemContainer>
    );
  });
  return <Wrapper $design={design}>{PhaseItems}</Wrapper>;
};

export default PhaseProgress;
